import { forwardRef, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { MessageFormEmailPhoneData, MessageFormEmailPhoneProps } from './types'
import { MessageComponent } from './message-component'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/react-hook-form'
import { GdprSubmit } from 'components/gdpr-submit'
import { createEvent, restore } from 'effector'
import { resetMessageForm } from './message-form.events'
import { useUnit } from 'effector-react'
import { getGdprSchema, GdprFormValues } from '../gdpr-submit/gdpr-submit'

const doSubmitValues = createEvent<MessageFormEmailPhoneData | null>()
const $submittedValues = restore(doSubmitValues, null).reset(resetMessageForm)

export const MessageFormEmailPhone = forwardRef<
  HTMLDivElement,
  MessageFormEmailPhoneProps
>(({ onSubmit, type, t, phoneCountryCode }, ref) => {
  const data = useUnit($submittedValues)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const schema: yup.ObjectSchema<MessageFormEmailPhoneData & GdprFormValues> =
    getGdprSchema(t).concat(
      yup.object().shape({
        name: yup.string().required(t('welcome.inputs.errors.name')),
        surname: yup.string().required(t('welcome.inputs.errors.surname')),
        email: yup
          .string()
          .matches(
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            t('welcome.inputs.errors.email.invalid')
          )
          .required(t('welcome.inputs.errors.email.required')),
        phone: yup
          .string()
          .required(t('welcome.inputs.errors.phone.required'))
          .test(
            'isPossiblePhoneNumber',
            t('welcome.inputs.errors.phone.invalid'),
            (value) => (value ? isPossiblePhoneNumber(value, 'RU') : false)
          )
      })
    )

  const formData = useForm<MessageFormEmailPhoneData & GdprFormValues>({
    defaultValues: {
      phone: '+' + phoneCountryCode.replace(/\D/g, '')
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit'
  })

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = formData

  const onSubmitForm = async (data: MessageFormEmailPhoneData) => {
    setIsSubmitting(true)
    await onSubmit(data)
    doSubmitValues(data)
    setIsSubmitting(false)
  }

  if (data) {
    return (
      <MessageComponent
        text={t('welcome.message_bubbles.form_email_phone.text_after_submit', {
          name: data?.name,
          surname: data?.surname,
          email: data?.email,
          phone: data?.phone
        })}
        type={type}
        ref={ref}
      />
    )
  }

  return (
    <div
      ref={ref}
      className="mx-auto mt-10 max-w-md rounded-[14px] bg-white p-6"
    >
      <h2 className="mb-6 text-left text-[17px] text-gray-800">
        {t('welcome.message_bubbles.form_email_phone.header')}
      </h2>

      <FormProvider {...formData}>
        <form
          autoComplete="on"
          onSubmit={handleSubmit(onSubmitForm)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder={t('welcome.inputs.placeholders.name')}
                  autoComplete="given-name"
                  className={`w-full rounded-xl border bg-white p-3 ${
                    errors[field.name] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              )}
            />

            <Controller
              name="surname"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder={t('welcome.inputs.placeholders.surname')}
                  autoComplete="family-name"
                  className={`w-full rounded-xl border bg-white p-3 ${
                    errors[field.name] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              )}
            />
          </div>

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="email"
                placeholder={t('welcome.inputs.placeholders.email')}
                autoComplete="email"
                className={`w-full rounded-xl border bg-white p-3 ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                {...field}
                control={control}
                // defaultCountry={defineCountryByPhonePrefix(phoneCountryCode)}
                placeholder={t('welcome.inputs.placeholders.phone')}
                className={`w-full rounded-xl border bg-white p-3 ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-300'
                }`}
                limitMaxLength
                international
              />
            )}
          />

          <div className="mt-6 text-left text-sm text-gray-500">
            {t('welcome.inputs.description.external_contact')}
          </div>

          <GdprSubmit submitting={isSubmitting} />
        </form>
      </FormProvider>
    </div>
  )
})

MessageFormEmailPhone.displayName = 'MessageFormEmailPhone'
